import { createFileRoute } from '@tanstack/react-router'
import React, { useCallback, useRef, useState } from 'react'
import classNames from 'classnames'

import { DanceInstructionsQuery } from '@/types/gql/graphql'

import { backendQueryHook, cleanMetadataValues, graphql } from '@/backend'
import { sortDances, usePatchDance } from '@/services/dances'
import { useCallbackOnEventChanges } from '@/services/events'

import { formFor, patchStrategy, Switch, SyncStatus, useAutosavingState } from '@/libraries/forms'
import { isEmptyDocument } from '@/libraries/lexical'
import { DocumentViewer } from '@/libraries/lexical/DocumentViewer'
import { Button, H2 } from '@/libraries/ui'
import { showToast } from '@/libraries/ui/hooks'
import { InstructionEditor } from '@/components/dance/DanceEditor'
import { WikipageSelector } from '@/components/dance/WikipageSelector'
import { LoadingState } from '@/components/LoadingState'
import { PrintViewToolbar } from '@/components/print'
import { useFormatDateTime, useT, useTranslation } from '@/i18n'
import { selectElement } from '@/utils/selectElement'
import { uniq } from '@/utils/uniq'

import './dance-instructions.sass'

export const Route = createFileRoute(
  '/events/$eventId/{-$eventVersionId}/print/dance-instructions',
)({
  component: RouteComponent,
})

type Workshop = NonNullable<DanceInstructionsQuery['event']>['workshops'][0]
type Dance = NonNullable<Workshop['instances'][0]['dances']>[0]
type EditableDance = Pick<Dance, 'description' | 'instructions' | 'wikipageName'>

const { Form, Field } = formFor<EditableDance>()

function RouteComponent() {
  const eventId = Route.useParams().eventId
  const t = useT('routes.events.event.print.danceInstructions')
  const dancesEl = useRef<HTMLElement>(null)
  const [showWorkshops, setShowWorkshops] = useState(true)
  const [showDances, setShowDances] = useState(true)
  const [showShortInstructions, setShowShortInstructions] = useState(false)
  const [hilightEmpty, setHilightEmpty] = useState(false)

  function selectAndCopy() {
    selectElement(dancesEl.current)
    document.execCommand('copy')
    showToast({ message: t('instructionsCopied') })
    window.getSelection()?.removeAllRanges()
  }

  return <>
    <PrintViewToolbar maxHeight={180}>
      <p>{t('clickInstructionsToEdit')}</p>
      <p>{t('defaultStylingDescription')}</p>
      <p>
        <Switch id="showWorkshops" inline label={t('showWorkshops')} value={showWorkshops} onChange={setShowWorkshops} />
        <Switch id="showDances" inline label={t('showDances')} value={showDances} onChange={setShowDances} />
        {showDances &&
          <>
            <Switch id="showShortInstructions" inline label={t('showShortInstructions')} value={showShortInstructions} onChange={setShowShortInstructions} />
            <Switch id="hilightEmpty" inline label={t('hilightEmpty')} value={hilightEmpty} onChange={setHilightEmpty} />
          </>
        }
        <Button text={t('selectAndCopy')} onClick={selectAndCopy} />
        <Button text={t('print')} onClick={() => window.print()} />
      </p>
    </PrintViewToolbar>
    <DanceInstructionsView
      eventId={eventId}
      elementRef={dancesEl}
      hilightEmpty={hilightEmpty}
      showWorkshops={showWorkshops}
      showDances={showDances}
      showShortInstructions={showShortInstructions}
    />
  </>
}

function DanceInstructionsView({ eventId, showWorkshops, showDances, hilightEmpty, showShortInstructions, elementRef }) {
  const t = useT('routes.events.event.print.danceInstructions')
  const { data, refetch, ...loadingState } = useDanceInstructions({ eventId })
  const formatDateTime = useFormatDateTime()

  if (!data?.event) return <LoadingState {...loadingState} refetch={refetch} />

  const { workshops, program } = data.event
  const dances = getDances(workshops)

  return <section className={classNames('dance-instructions', { 'hilight-empty': hilightEmpty, showShortInstructions })} ref={elementRef}>
    {showWorkshops &&
      <section className="workshops">
        <h1 className={h1Class}>{t('workshopsAndBall')}</h1>
        {workshops.map(workshop => <WorkshopDetails key={workshop._id} workshop={workshop} />)}
        {program.dateTime && <>
          <H2>
            {t('ball')}
          </H2>
          <p className="font-bold">{formatDateTime(program.dateTime)}</p>
        </>}
      </section>
    }
    {showDances &&
      <section className="dances">
        <h1 className={h1Class}>{t('danceInstructions')}</h1>
        {dances.map(dance => <InstructionsForDance key={dance._id} dance={dance} showShortInstructions={showShortInstructions} />)}
      </section>
    }
  </section>
}

const h1Class = 'font-bold my-2 text-3xl'

const useDanceInstructions = backendQueryHook(graphql(`
query DanceInstructions($eventId: ID!) {
  event(id: $eventId) {
    _id
    _versionId
    program {
      dateTime
    }
    workshops {
      _id
      _versionId
      name
      description
      instanceSpecificDances
      instances {
        _id
        dateTime
        dances {
          _id
          name
          description
          instructions
          wikipageName
          wikipage {
            content
          }
        }
      }
    }
  }
}`), ({ refetch, variables }) => {
  if (variables === undefined) throw new Error('Unknown event id')
  useCallbackOnEventChanges(variables.eventId, refetch)
})

interface Instance {
  dances?: { name: string, _id: string }[] | null
}

function getDances(workshops: { instances: Instance[] }[]) {
  const dances = uniq(workshops.flatMap(w => w.instances).flatMap(i => i.dances ?? []))
  return sortDances(dances)
}

function InstructionsForDance({ dance, showShortInstructions }: { dance: Pick<Dance, '_id' | 'name' | 'description' | 'instructions' | 'wikipageName'>, showShortInstructions: boolean }) {
  const [editorOpen, setEditorOpen] = useState(false)
  const field = showShortInstructions ? 'description' : 'instructions' as const
  const value = dance[field] ?? null

  return <div className={`dance-instructions-dance ${isEmptyDocument(value) ? 'empty' : 'not-empty'}`}>
    <H2>
      {dance.name}
      <Button
        requireRight="dances:modify"
        entityId={dance._id}
        color="primary"
        minimal
        icon="edit"
        text={useTranslation(editorOpen ? 'common.closeEditor' : 'common.edit')}
        iconOnly
        onClick={() => setEditorOpen(!editorOpen)}
      />
    </H2>
    <div className={field}>
      {editorOpen
        ? <DanceFieldEditor dance={dance} field={field} />
        : <DocumentViewer document={value} skipHeadingLevels={2} />
      }
    </div>
  </div>
}

function DanceFieldEditor({ dance: danceInDatabase, field }: { dance: Pick<Dance, '_id' | 'name' | 'description' | 'instructions' | 'wikipageName' | 'wikipage'>, field: 'description' | 'instructions' }) {
  const t = useT('domain.dance')
  const [patchDance] = usePatchDance()
  const onPatch = useCallback(
    (dance: unknown[]) => patchDance({
      id: danceInDatabase._id,
      dance,
    }),
    [danceInDatabase, patchDance],
  )
  const { value, onChange, state } = useAutosavingState<EditableDance, unknown[]>(cleanMetadataValues<Dance>(danceInDatabase), onPatch, patchStrategy.jsonPatch)

  return <Form value={value} onChange={onChange}>
    <SyncStatus state={state} floatRight />
    <Field
      label={t(field)}
      path={field}
      component={InstructionEditor}
      componentProps={{ danceId: danceInDatabase._id, wikipage: danceInDatabase.wikipage }}
    />
    <Field label={t('wikipageName')} path="wikipageName" component={WikipageSelector} componentProps={{ possibleName: danceInDatabase.name }} />
  </Form>
}

function WorkshopDetails({ workshop }: { workshop: Workshop }) {
  const t = useT('routes.events.event.print.danceInstructions')
  const { name, description, instanceSpecificDances, instances } = workshop
  const formatDateTime = useFormatDateTime()

  return <div className="workshop">
    <H2>
      {name}
    </H2>
    <p className="description"><DocumentViewer document={description} skipRenderOnEmpty /></p>
    {instanceSpecificDances
      ? instances.map(instance =>
        <React.Fragment key={instance._id}>
          <h3 className="font-bold">{formatDateTime(new Date(instance.dateTime))}</h3>
          {!!instance?.dances?.length &&
            <p>
              {t('dances')}: {instance?.dances?.map(d => d.name)?.join(', ')}
            </p>
          }
        </React.Fragment>,
      )
      : <>
        <h3 className="font-bold">{instances.map(instance => formatDateTime(new Date(instance.dateTime))).join(', ')}</h3>
        {!!instances[0]?.dances?.length &&
          <p>{t('dances') + ': '}{instances[0]?.dances?.map(d => d.name)?.join(', ')}</p>
        }
      </>
    }
  </div>
}
